import PageHeader from "@/components/PageHeader";
import { Stack, Grid2 as Grid, Container } from "@mui/material";
import { useParams } from "react-router-dom";
import { useFetchWeeklyArchiveOrdersQuery } from "@/features/organizations/api/ordersApi";
import { skipToken } from "@reduxjs/toolkit/query";
import useOrderStats from "@/hooks/useOrderStats";
import { Order } from "@/types";
import OrderSummary from "./components/OrderSummary";
import OrdersPerWeek from "./components/OrdersPerWeek";
import ChartsToggle from "./components/PieChartsToggle";
import DisabledDishes from "./components/DisabledDishes";
import StatsFilters from "./components/StatsFilters";
import { useState } from "react";
import { DATE_RANGE, DATE_RANGES } from "@/constants/global";
import CustomersHeadmap from "./components/CustomersHeadmap";
import ErrorBoundary from "@/components/ErrorBoundary";
import TimingHeadmap from "./components/TimingHeadmap";
import { useAppSelector } from "@/hooks";
import { Branch } from "@/types/branch";
import TutorialsSubscriber from "@/components/TutorialsSubscriber";
import { ProductIds } from "@/constants/productIds";

const DashboardPage = () => {
  const container = useAppSelector((state) => state.ui?.container);
  const [dateRange, setDateRange] = useState<DATE_RANGE>(DATE_RANGE.LAST_SEVEN_DAYS);
  const { organizationId } = useParams<{ organizationId: string }>();
  const organization = useAppSelector((state) => state.organizations[organizationId])
  const activeBranch = useAppSelector((state) => state.ui.activeBranch);
  const weeklyArchivedOrders = useFetchWeeklyArchiveOrdersQuery({
    organizationId: organizationId || skipToken,
    dateRange: dateRange || skipToken,
  });
  const { totalOrders, totalClients, totalProfits, ordersByDay, paymentPercentages, uniqueCustomers, completionRate } = useOrderStats(weeklyArchivedOrders?.data as Order[] | undefined, DATE_RANGES[dateRange].days);
  const branchName = organization?.branches?.find((branch: Branch) => branch.id === activeBranch)?.title;

  return (
    <Stack spacing={3} height={'100vh'}>
      <PageHeader
        title={activeBranch ? `Resumen de Pedidos - ${branchName}` : "Resumen de Pedidos"}
        action={
          <StatsFilters onChange={(selectedDateRange) => {
            setDateRange(selectedDateRange);
          }} />
        }
      />
      <Stack flex={1}>
        <Container
          maxWidth={container === 'fixed' ? 'lg' : false}
          sx={{
            height: '100%',
          }}
        >
          <Stack spacing={3} height={'100%'}>
            <OrderSummary isLoading={weeklyArchivedOrders.isLoading} totalOrders={totalOrders} totalClients={totalClients} totalProfits={totalProfits} />

            <Grid
              container
              direction={"row"}
              spacing={{
                xs: 2,
                lg: 3,
              }}
            >

              <Grid container direction="column" size={{ xs: 12, lg: 8 }} spacing={2}>
                <ErrorBoundary>
                  <OrdersPerWeek isLoading={weeklyArchivedOrders.isLoading} data={ordersByDay} />
                </ErrorBoundary>
                <ErrorBoundary>
                  <CustomersHeadmap customers={uniqueCustomers} />
                </ErrorBoundary>
                <DisabledDishes key={activeBranch || null} />
              </Grid>
              <Grid container direction="column" size={{ xs: 12, lg: 4 }} spacing={2}>
                <ErrorBoundary>
                  <ChartsToggle isLoading={weeklyArchivedOrders.isLoading} paymentData={paymentPercentages} completionData={completionRate} />
                </ErrorBoundary>
                <ErrorBoundary>
                  <TimingHeadmap data={weeklyArchivedOrders.data} />
                </ErrorBoundary>
              </Grid>
            </Grid>
            <TutorialsSubscriber productIds={[ProductIds.ANALYTICS]} />
          </Stack>
        </Container>
      </Stack>
    </Stack>
  );
};

export default DashboardPage;
